import { SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Empty, Input, Typography, theme } from 'antd';
import type React from 'react';
import { memo, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import type {
  ParameterSelection_DoctorSheetFragment,
  ParameterSelection_RequestableParameterFragment,
  ParameterToAddInput,
} from '../../../graphql/generated/graphql.ts';
import type { SheetData } from '../../../graphql/sheet.ts';
import type { SelectedParameter } from '../../../hooks/store/useAppStore.ts';
import { SheetAccordion } from './sheets/SheetAccordion.tsx';
import { countSheetDataMatches } from './sheets/sheetSearch.ts';

export type SelectedParamInfo = {
  selectedParamMainIds: Record<string, string>;
  withoutParameterIds: Record<string, string>;
};

const Sheets: React.FC<{
  sheets: readonly ParameterSelection_DoctorSheetFragment[];
  params: readonly ParameterSelection_RequestableParameterFragment[];
  selectedParams: SelectedParameter[];
  onAdd: (inputs: ParameterToAddInput[]) => void;
  onRemove: (paramId: string) => void;
  onInfo: (param: ParameterSelection_RequestableParameterFragment) => void;
  reorder: boolean;
  flipParamNames: boolean;
}> = ({ sheets, params, selectedParams, onAdd, onRemove, onInfo, reorder, flipParamNames }) => {
  const { token } = theme.useToken();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const highlight = debouncedSearch ?? '';

  const doctorSheet = useMemo(() => sheets.find(it => !!it.doctorId), [sheets]);
  const labSheets = useMemo(() => sheets.filter(it => !!it.labId), [sheets]);

  // Sheets highlight rather than filter (the layout mirrors the paper form), so a search with
  // no hits would otherwise be indistinguishable from "nothing happened".
  const totalMatches = useMemo(() => {
    const tokens = tokenize(highlight);
    if (!tokens.length) {
      return 0;
    }
    return sheets.reduce(
      (sum, sheet) => sum + sheet.data.reduce((s: number, sheetData: SheetData) => s + countSheetDataMatches(sheetData, tokens), 0),
      0
    );
  }, [sheets, highlight]);

  const styles = useMemo(
    () => ({
      root: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        gap: ${token.marginXS}px;
      `,
      list: css`
        flex: 1;
        min-height: 0;
        overflow-y: auto;
      `,
    }),
    [token]
  );

  /** performance improvement */
  const sheetParamLookup = useMemo(() => {
    const map: Record<string, ParameterSelection_RequestableParameterFragment | undefined> = {};
    sheets.forEach(sheet => {
      sheet.data.forEach((sheetData: SheetData) => {
        sheetData.groups.forEach(group => {
          group.parameters.forEach(sheetParam => {
            map[sheetParam.id] = params.find(it => it.mainParameterId === sheetParam.id);
          });
        });
      });
    });
    return map;
  }, [params, sheets]);

  /** performance improvement */
  const selectedParamInfo: SelectedParamInfo = useMemo(
    () => ({
      selectedParamMainIds: Object.fromEntries(selectedParams.map(it => [it.mainParameterId, it.mainParameterId])),
      withoutParameterIds: Object.fromEntries(selectedParams.flatMap(sp => sp.withoutParameterIds).map(v => [v, v])),
    }),
    [selectedParams]
  );

  if (!doctorSheet?.data.length && !labSheets.some(it => it.data.length)) {
    return <Empty description="Keine Sheets verfügbar" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className={styles.root}>
      <Input
        allowClear
        placeholder="Suche nach Bezeichnung"
        value={search ?? ''}
        onChange={e => setSearch(e.target.value)}
        prefix={<SearchOutlined />}
      />
      {highlight && totalMatches === 0 && <Typography.Text type="secondary">Keine Treffer in den Anforderungsscheinen</Typography.Text>}
      <div className={styles.list}>
        {doctorSheet && doctorSheet.data.length > 0 && (
          <SheetAccordion
            sheet={doctorSheet}
            sheetParamLookup={sheetParamLookup}
            selectedParamInfo={selectedParamInfo}
            onAdd={onAdd}
            onRemove={onRemove}
            onInfo={onInfo}
            reorder={reorder}
            flipParamNames={flipParamNames}
            highlight={highlight}
          />
        )}
        {labSheets
          .filter(it => it.data.length > 0)
          .map(labSheet => (
            <SheetAccordion
              key={labSheet.labId}
              sheet={labSheet}
              sheetParamLookup={sheetParamLookup}
              selectedParamInfo={selectedParamInfo}
              onAdd={onAdd}
              onRemove={onRemove}
              onInfo={onInfo}
              reorder={reorder}
              flipParamNames={flipParamNames}
              highlight={highlight}
            />
          ))}
      </div>
    </div>
  );
};

export const MemoSheets = memo(Sheets);
